import type { PhysicalObject, Point, ProjectionAwarenessSettings, TableEdge } from "../types";

export interface ScreenPhysicalObject {
  id: string;
  center: Point;
  shape: PhysicalObject["shape"];
  widthPx: number;
  depthPx: number;
  heightPx: number;
  pixelsPerInch: number;
  rotation: number;
  blocksProjection: boolean;
  blocksWeather: boolean;
}

const rounded = (value: number) => Number.isFinite(value) ? Number(value.toFixed(2)) : 0;

function edgeVector(edge: TableEdge | "overhead"): Point {
  if (edge === "north") return { x: 0, y: 1 };
  if (edge === "south") return { x: 0, y: -1 };
  if (edge === "west") return { x: 1, y: 0 };
  if (edge === "east") return { x: -1, y: 0 };
  return { x: 0, y: 0 };
}

function rotate(point: Point, angle: number): Point {
  const cosine = Math.cos(angle); const sine = Math.sin(angle);
  return { x: point.x * cosine - point.y * sine, y: point.x * sine + point.y * cosine };
}

function convexHull(points: Point[]): Point[] {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  if (sorted.length <= 2) return sorted;
  const cross = (origin: Point, a: Point, b: Point) => (a.x - origin.x) * (b.y - origin.y) - (a.y - origin.y) * (b.x - origin.x);
  const lower: Point[] = [];
  for (const point of sorted) { while (lower.length >= 2 && cross(lower.at(-2)!, lower.at(-1)!, point) <= 0) lower.pop(); lower.push(point); }
  const upper: Point[] = [];
  for (const point of sorted.reverse()) { while (upper.length >= 2 && cross(upper.at(-2)!, upper.at(-1)!, point) <= 0) upper.pop(); upper.push(point); }
  lower.pop(); upper.pop(); return [...lower, ...upper];
}

export function physicalObjectScreenPolygon(object: ScreenPhysicalObject, settings: ProjectionAwarenessSettings): Point[] {
  const margin = Math.max(0, settings.safetyMarginInches) * Math.max(1, object.pixelsPerInch);
  const halfWidth = Math.max(2, object.widthPx / 2 + margin);
  const halfDepth = Math.max(2, object.depthPx / 2 + margin);
  const angle = object.rotation * Math.PI / 180;
  const local = object.shape === "ellipse"
    ? Array.from({ length: 24 }, (_, index) => { const phase = index / 24 * Math.PI * 2; return { x: Math.cos(phase) * halfWidth, y: Math.sin(phase) * halfDepth }; })
    : [{ x: -halfWidth, y: -halfDepth }, { x: halfWidth, y: -halfDepth }, { x: halfWidth, y: halfDepth }, { x: -halfWidth, y: halfDepth }];
  const base = local.map((point) => { const next = rotate(point, angle); return { x: object.center.x + next.x, y: object.center.y + next.y }; });
  const direction = edgeVector(settings.projectorEdge);
  const spill = Math.max(0, object.heightPx * settings.spillPerHeight);
  const offset = { x: direction.x * spill, y: direction.y * spill };
  return convexHull([...base, ...base.map((point) => ({ x: point.x + offset.x, y: point.y + offset.y }))]);
}

export function projectionExclusionMask(objects: ScreenPhysicalObject[], width: number, height: number, settings: ProjectionAwarenessSettings, weatherOnly = false, polygonHoles: Point[][] = []): string | undefined {
  if (width <= 0 || height <= 0) return undefined;
  const applicable = settings.enabled ? objects.filter((object) => weatherOnly ? object.blocksWeather : object.blocksProjection) : [];
  const shapes = [...polygonHoles, ...applicable.map((object) => physicalObjectScreenPolygon(object, settings))];
  if (!shapes.length) return undefined;
  const holes = shapes.filter((polygon) => polygon.length >= 3).map((polygon) => `M${polygon.map((point) => `${rounded(point.x)} ${rounded(point.y)}`).join("L")}Z`).join("");
  if (!holes) return undefined;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${rounded(width)} ${rounded(height)}"><path fill="white" fill-rule="evenodd" d="M0 0H${rounded(width)}V${rounded(height)}H0Z${holes}"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

export function physicalDimensionsInGrid(object: PhysicalObject, gridInches: number) {
  const inchesPerGrid = Math.max(0.01, gridInches);
  return { width: object.widthInches / inchesPerGrid, depth: object.depthInches / inchesPerGrid, height: object.heightInches / inchesPerGrid };
}

/** Resize from the object's rotated bottom-right corner while its opposite
 * corner remains fixed. The supplied object must be the drag-start snapshot. */
export function resizePhysicalObjectFromBottomRight(object: PhysicalObject, pointer: Point, gridInches: number, keepProportions: boolean): PhysicalObject {
  const inchesPerGrid = Math.max(0.01, gridInches); const dimensions = physicalDimensionsInGrid(object, inchesPerGrid); const angle = object.rotation * Math.PI / 180;
  const anchorOffset = rotate({ x: -dimensions.width / 2, y: -dimensions.depth / 2 }, angle);
  const anchor = { x: object.position.x + anchorOffset.x, y: object.position.y + anchorOffset.y };
  const local = rotate({ x: pointer.x - anchor.x, y: pointer.y - anchor.y }, -angle);
  let widthInches = Math.max(0.25, Math.min(48, local.x * inchesPerGrid)); let depthInches = Math.max(0.25, Math.min(48, local.y * inchesPerGrid));
  if (keepProportions) {
    const scale = Math.max(widthInches / Math.max(0.25, object.widthInches), depthInches / Math.max(0.25, object.depthInches));
    const minimumScale = Math.max(0.25 / Math.max(0.25, object.widthInches), 0.25 / Math.max(0.25, object.depthInches));
    const maximumScale = Math.min(48 / Math.max(0.25, object.widthInches), 48 / Math.max(0.25, object.depthInches));
    const boundedScale = Math.max(minimumScale, Math.min(maximumScale, scale)); widthInches = object.widthInches * boundedScale; depthInches = object.depthInches * boundedScale;
  }
  widthInches = Number(widthInches.toFixed(2)); depthInches = Number(depthInches.toFixed(2));
  const centerOffset = rotate({ x: widthInches / inchesPerGrid / 2, y: depthInches / inchesPerGrid / 2 }, angle);
  return { ...object, widthInches, depthInches, position: { x: anchor.x + centerOffset.x, y: anchor.y + centerOffset.y } };
}

/** Hit-test the measured footprint instead of a hidden axis-aligned editor box. */
export function pointInPhysicalObject(point: Point, object: PhysicalObject, gridInches: number, toleranceGrid = 0): boolean {
  const dimensions = physicalDimensionsInGrid(object, gridInches);
  const angle = -object.rotation * Math.PI / 180;
  const local = rotate({ x: point.x - object.position.x, y: point.y - object.position.y }, angle);
  const halfWidth = Math.max(0.01, dimensions.width / 2 + toleranceGrid);
  const halfDepth = Math.max(0.01, dimensions.depth / 2 + toleranceGrid);
  if (object.shape === "rectangle") return Math.abs(local.x) <= halfWidth && Math.abs(local.y) <= halfDepth;
  return (local.x * local.x) / (halfWidth * halfWidth) + (local.y * local.y) / (halfDepth * halfDepth) <= 1;
}
